/**
 * Loads the tank .obj parts and assembles them into reusable prefabs.
 *
 * BASE TANK: four parts (treads, body, turret, barrel end) exported from one Roblox
 * model — every vertex is already in its assembled world position, so we apply ONE
 * shared recenter to all of them (footprint centered at origin, feet at y=0, barrel
 * toward -Z = "forward").
 *
 * PER-TYPE TURRETS (tank/othertanks): each enemy type can wear its own turret. The
 * turrets were laid out SIDE BY SIDE in the editor (each at its own X) but every one
 * shares the base tank's Z=90 rotation point + Y floor. So each turret is recentered
 * by (-(its OWN X-center), -42.5, -90): its X-center lands on the base tank's pivot —
 * the exact point the base turret rotates about — NOT the turret's front-to-back
 * midpoint. Each type's firepoint.obj marks its muzzle (rendered nowhere; just a
 * point), so e.g. the sniper's longer barrel pushes its muzzle further forward.
 *
 * STATIONARY EMPLACEMENTS: stationary types ride a legged base (stationary_base:
 * basemain + baselegs) instead of treads + hull.
 *
 * Recolor rules: treads/legs/barrel end → fixed dark; body/turret → tinted per type.
 */

import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";

const BASE = "/models/tank/";

// The shared base recenter: parts live around X=-145, Z=90, tread bottoms at Y=42.5.
const RECENTER = new THREE.Vector3(145, -42.5, -90);
// every part shares the base tank's vertical floor + Z rotation point; only X differs.
const PART_Y = -42.5, PART_Z = -90;

// Muzzle tip in the base turret's local frame (barrel end front face, forward = -Z).
export const MUZZLE_LOCAL = new THREE.Vector3(0, 6.875, -15);

// ---- MECHA walker body ----------------------------------------------------
// base + a leg (legtop hip→knee, legbottom knee→foot) share one assembled frame.
// MECHA_R recenters XZ to the base center and leaves Y as-is, so FEET (assembled Y≈0) sit
// at y≈0 and the base towers ~41–48 above → root at floorY gives a standing walker, feet on
// the ground. The whole walker (base + legs + turret) lives under bodyGroup, which the entity
// LOWERS to crouch the base to the floor (legs splay to keep the feet down).
const MECHA_R = new THREE.Vector3(-84.754, 0, -151.25);
// pivots in the recentered frame (modeled leg = the −X/−Z corner; mirror for the others).
const MECHA_HIP = new THREE.Vector3(81.0614 - 84.754, 47.3113, 145 - 151.25);   // base↔legtop
const MECHA_KNEE = new THREE.Vector3(62.5 - 84.754, 28.232, 143.75 - 151.25);    // legtop↔legbottom
const MECHA_FOOT = new THREE.Vector3(60 - 84.754, -0.264, 142.5 - 151.25);       // foot (ground hit target)
const MECHA_TURRET_Y = 44; // turret mount height on the base platform top (tuned visually)
// bone lengths + local axes for the 2-bone IK (legtop is pre-translated so the hip is at its
// origin; legbottom so the knee is at its origin — each bone's local axis points down the bone).
const MECHA_L1 = MECHA_HIP.distanceTo(MECHA_KNEE);
const MECHA_L2 = MECHA_KNEE.distanceTo(MECHA_FOOT);
const MECHA_UP_AXIS = new THREE.Vector3().subVectors(MECHA_KNEE, MECHA_HIP).normalize();
const MECHA_LO_AXIS = new THREE.Vector3().subVectors(MECHA_FOOT, MECHA_KNEE).normalize();
// rest bend-plane normal (shared by both bones) — the IK aligns this to the live bend plane so the
// ASYMMETRIC leg meshes keep their designed roll and the knee spikes never twist out of plane.
const MECHA_REST_NORMAL = new THREE.Vector3().crossVectors(MECHA_UP_AXIS, MECHA_LO_AXIS).normalize();

export interface TurretVariant {
  turret: THREE.BufferGeometry;
  fire: THREE.BufferGeometry; // the firepoint mesh — rendered dark as the barrel TIP (like the base barrel end)
  muzzles: THREE.Vector3[]; // one or more muzzle points in the turret-pivot frame
  spin?: { geo: THREE.BufferGeometry; y: number }; // gatling: a barrel cluster that spins about the firing axis (at height y)
}

export interface TankGeometries {
  treads: THREE.BufferGeometry;
  body: THREE.BufferGeometry;
  turret: THREE.BufferGeometry;
  barrelEnd: THREE.BufferGeometry;
  baseMain: THREE.BufferGeometry; // stationary emplacement: colored top platform
  baseLegs: THREE.BufferGeometry; // stationary emplacement: fixed dark legs
  mecha: { base: THREE.BufferGeometry; up: THREE.BufferGeometry; lo: THREE.BufferGeometry }; // walker: base + hip-origin legtop + knee-origin legbottom
  variants: Record<string, TurretVariant>;
}

interface VariantDef { id: string; barrels?: number; spin?: boolean; }
const VARIANT_DEFS: VariantDef[] = [
  { id: "marauder" },
  { id: "sniper-lurker" },
  { id: "laser" },
  { id: "artillery" },
  { id: "aegis" },
  { id: "gatling", spin: true },
  { id: "tow_x1" },
  { id: "tow_x2", barrels: 2 },
  { id: "tow_x3", barrels: 3 },
];

// enemy type id -> turret variant id (absent = base turret). `tow` tiers by floor.
const TURRET_BY_TYPE: Record<string, string> = {
  marauder: "marauder",
  sniper: "sniper-lurker",
  stalker: "sniper-lurker",
  laserbeam: "laser",
  artillery: "artillery",
  mlrs: "artillery", // MLRS reuses the howitzer turret (no dedicated model yet)
  shieldgen: "aegis",
  gatling: "gatling",
};
// stationary types ride a legged emplacement base instead of treads + hull.
const STATIC_BODY = new Set(["brown", "teal", "green", "rocket", "sniper", "laserbeam", "mlrs"]);

/** TOW turret by 0-based FLOOR INDEX: more barrels the deeper you go. */
function towVariantId(layerIdx: number): string {
  return layerIdx >= 39 ? "tow_x3" : layerIdx >= 29 ? "tow_x2" : "tow_x1";
}
/** The turret variant id for a type at a floor index, or null for the base turret. */
export function turretVariantId(typeId: string | undefined, layerIdx: number): string | null {
  if (!typeId) return null;
  if (typeId === "tow") return towVariantId(layerIdx);
  return TURRET_BY_TYPE[typeId] ?? null;
}
/** Does this type ride a legged emplacement base (vs treads)? */
export function usesStaticBody(typeId: string | undefined): boolean {
  return !!typeId && STATIC_BODY.has(typeId);
}

let cache: TankGeometries | null = null;
let inflight: Promise<TankGeometries> | null = null;

/** Load one .obj into a single BufferGeometry (textures stripped), NOT recentered. */
async function loadRaw(file: string): Promise<THREE.BufferGeometry> {
  const obj = await new OBJLoader().loadAsync(BASE + file);
  obj.updateMatrixWorld(true);
  const parts: THREE.BufferGeometry[] = [];
  obj.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    const g = (o.geometry.index ? o.geometry.toNonIndexed() : o.geometry.clone()) as THREE.BufferGeometry;
    g.applyMatrix4(o.matrixWorld);
    const keep = new THREE.BufferGeometry();
    keep.setAttribute("position", g.getAttribute("position"));
    if (g.getAttribute("normal")) keep.setAttribute("normal", g.getAttribute("normal"));
    parts.push(keep);
  });
  let geo = parts.length === 1 ? parts[0] : mergeGeometries(parts, false);
  if (!geo) geo = parts[0];
  if (!geo.getAttribute("normal")) geo.computeVertexNormals();
  return geo;
}

/** Load a base part: raw geometry shifted by the shared base recenter. */
async function loadPart(file: string): Promise<THREE.BufferGeometry> {
  const geo = await loadRaw(file);
  geo.translate(RECENTER.x, RECENTER.y, RECENTER.z);
  return geo;
}

/** Muzzle point from a firepoint box: front-face (min-Z) center. */
function muzzleOf(geo: THREE.BufferGeometry): THREE.Vector3 {
  geo.computeBoundingBox();
  const bb = geo.boundingBox!;
  return new THREE.Vector3((bb.min.x + bb.max.x) / 2, (bb.min.y + bb.max.y) / 2, bb.min.z);
}

/** Split a multi-barrel firepoint into one muzzle per barrel (binned by X). */
function splitMuzzles(geo: THREE.BufferGeometry, barrels: number): THREE.Vector3[] {
  geo.computeBoundingBox();
  const bb = geo.boundingBox!;
  const minX = bb.min.x, span = (bb.max.x - bb.min.x) || 1;
  const yc = (bb.min.y + bb.max.y) / 2, mz = bb.min.z;
  const sum = new Array(barrels).fill(0), cnt = new Array(barrels).fill(0);
  const pos = geo.getAttribute("position");
  for (let i = 0; i < pos.count; i++) {
    let b = Math.floor(((pos.getX(i) - minX) / span) * barrels);
    if (b >= barrels) b = barrels - 1; if (b < 0) b = 0;
    sum[b] += pos.getX(i); cnt[b]++;
  }
  const out: THREE.Vector3[] = [];
  for (let b = 0; b < barrels; b++) {
    const cx = cnt[b] ? sum[b] / cnt[b] : minX + (span * (b + 0.5)) / barrels;
    out.push(new THREE.Vector3(cx, yc, mz));
  }
  return out;
}

/** Load the stationary emplacement body (legs + top). Like the turrets, it sits at its
 *  own X in the editor, so recenter BOTH parts by the legs' X-center (→ the base pivot). */
async function loadStaticBase(): Promise<{ main: THREE.BufferGeometry; legs: THREE.BufferGeometry }> {
  const legs = await loadRaw("stationary_base/baselegs.obj");
  legs.computeBoundingBox();
  const bb = legs.boundingBox!;
  const cx = (bb.min.x + bb.max.x) / 2;
  legs.translate(-cx, PART_Y, PART_Z);
  const main = await loadRaw("stationary_base/basemain.obj");
  main.translate(-cx, PART_Y, PART_Z); // SAME recenter so the top stays aligned on the legs
  return { main, legs };
}

/** Load the mecha walker geometry: base + the two leg segments, recentered into the game
 *  frame and pre-pivoted (legtop → hip at origin, legbottom → knee at origin) so the entity
 *  can rotate them about the hip/knee for the walk cycle / crouch / topple. */
async function loadMecha(): Promise<TankGeometries["mecha"]> {
  const [base, up, lo] = await Promise.all([
    loadRaw("mecha/base/base.obj"),
    loadRaw("mecha/legs/legtop.obj"),
    loadRaw("mecha/legs/legbottom.obj"),
  ]);
  for (const g of [base, up, lo]) g.translate(MECHA_R.x, MECHA_R.y, MECHA_R.z);
  up.translate(-MECHA_HIP.x, -MECHA_HIP.y, -MECHA_HIP.z);    // legtop rotates about the hip
  lo.translate(-MECHA_KNEE.x, -MECHA_KNEE.y, -MECHA_KNEE.z); // legbottom rotates about the knee
  return { base, up, lo };
}

/** Load one per-type turret variant: turret + firepoint(s) recentered on the base pivot. */
async function loadVariant(def: VariantDef): Promise<TurretVariant> {
  const dir = def.id + "/";
  const turret = await loadRaw(dir + "turret.obj");
  turret.computeBoundingBox();
  const bb = turret.boundingBox!;
  const cx = (bb.min.x + bb.max.x) / 2; // this turret's X-center → maps to the base pivot
  const recenter = (g: THREE.BufferGeometry) => g.translate(-cx, PART_Y, PART_Z);
  recenter(turret);

  const fpFile = def.barrels ? "firepoints.obj" : "firepoint.obj";
  const fp = await loadRaw(dir + fpFile);
  recenter(fp);
  const muzzles = def.barrels ? splitMuzzles(fp, def.barrels) : [muzzleOf(fp)];

  let spin: TurretVariant["spin"] | undefined;
  if (def.spin) {
    const sg = await loadRaw(dir + "spinbarrel.obj");
    recenter(sg);
    sg.computeBoundingBox();
    const sbb = sg.boundingBox!;
    const y = (sbb.min.y + sbb.max.y) / 2; // firing-axis height; center the geo on it so it spins true
    sg.translate(0, -y, 0);
    spin = { geo: sg, y };
  }
  return { turret, fire: fp, muzzles, spin };
}

/** Load (once) every geometry: base parts, the stationary base, and all turret variants. */
export async function loadTankGeometries(): Promise<TankGeometries> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    const [treads, body, turret, barrelEnd, staticBase, mecha] = await Promise.all([
      loadPart("TankTreads.obj"),
      loadPart("TankBody.obj"),
      loadPart("TankTurret.obj"),
      loadPart("TankTurretBarrelEnd.obj"),
      loadStaticBase(),
      loadMecha(),
    ]);
    addTopUVs(body);
    const variantList = await Promise.all(VARIANT_DEFS.map(loadVariant));
    const variants: Record<string, TurretVariant> = {};
    VARIANT_DEFS.forEach((d, i) => (variants[d.id] = variantList[i]));
    cache = { treads, body, turret, barrelEnd, baseMain: staticBase.main, baseLegs: staticBase.legs, mecha, variants };
    return cache;
  })();
  return inflight;
}

/**
 * Add planar top-down UVs so an avatar texture maps onto the body as seen from
 * above. u from X, v from Z over the body's own bounds.
 */
function addTopUVs(geo: THREE.BufferGeometry) {
  geo.computeBoundingBox();
  const bb = geo.boundingBox!;
  const sx = bb.max.x - bb.min.x || 1;
  const sz = bb.max.z - bb.min.z || 1;
  const pos = geo.getAttribute("position");
  const uv = new Float32Array(pos.count * 2);
  for (let i = 0; i < pos.count; i++) {
    uv[i * 2] = (pos.getX(i) - bb.min.x) / sx;
    uv[i * 2 + 1] = 1 - (pos.getZ(i) - bb.min.z) / sz; // flip V so faces aren't mirrored
  }
  geo.setAttribute("uv", new THREE.BufferAttribute(uv, 2));
}

// fixed (never-recolored) materials, shared across every tank instance
const treadsMat = new THREE.MeshLambertMaterial({ color: 0x26201a });
const barrelMat = new THREE.MeshLambertMaterial({ color: 0x1c1c1c });

export interface BuiltTank {
  root: THREE.Group;
  turretPivot: THREE.Group;
  bodyMat: THREE.MeshLambertMaterial;
  turretMat: THREE.MeshLambertMaterial;
  muzzles: THREE.Vector3[]; // muzzle point(s) in the turret-pivot local frame
  spinBarrel?: THREE.Object3D; // gatling: spin this about its Z each frame while firing
  /** dispose the per-instance recolorable materials */
  dispose(): void;
}

/** One mecha leg: two rigid bone meshes (upper hip→knee, lower knee→foot) the entity orients
 *  every frame via 2-bone IK to reach a foot target, plus the footstep-controller state. */
export interface MechaLeg {
  upper: THREE.Mesh; // hip→knee bone (positioned at the hip, oriented toward the IK knee)
  lower: THREE.Mesh; // knee→foot bone (positioned at the knee, oriented toward the foot)
  corner: [number, number];
  hipLocal: THREE.Vector3; // hip attach point in bodyGroup-local (fixed)
  restFoot: THREE.Vector3; // neutral foot position in body-local (y=0) — the footstep target
  planted: THREE.Vector3;  // current foot WORLD position (the IK target + the hit target)
  stepFrom: THREE.Vector3; stepTo: THREE.Vector3;
  stepping: boolean; stepT: number;
  dodge: boolean; // this step is an evasive leg dodge (snappier than a normal stride step)
  alive: boolean;
}
export interface BuiltMecha extends BuiltTank {
  bodyGroup: THREE.Group; // base + turret; lowered to crouch (legs fold via IK to keep feet planted)
  legs: MechaLeg[];
  l1: number; l2: number;        // bone lengths (hip→knee, knee→foot)
  upAxis: THREE.Vector3;         // upper-bone local axis
  loAxis: THREE.Vector3;         // lower-bone local axis
  restNormal: THREE.Vector3;     // rest bend-plane normal = normalize(cross(upAxis, loAxis)); locks bone roll
}

/** Assemble a turret pivot for `typeId` (per-type variant or the base turret) — shared by
 *  buildTank + buildMecha. Returns the pivot + its meshes so the caller sets ghost/shadow. */
function buildTurretPivot(geo: TankGeometries, turretMat: THREE.MeshLambertMaterial, typeId?: string, layerIdx = 0) {
  const variantId = turretVariantId(typeId, layerIdx);
  const variant = variantId ? geo.variants[variantId] : null;
  const turretPivot = new THREE.Group();
  const turret = new THREE.Mesh(variant ? variant.turret : geo.turret, turretMat);
  let barrelEnd: THREE.Mesh;
  let spinBarrel: THREE.Object3D | undefined;
  turretPivot.add(turret);
  if (variant) {
    barrelEnd = new THREE.Mesh(variant.fire, barrelMat); // firepoint = the dark barrel tip
    if (variant.spin) {
      const grp = new THREE.Group();
      grp.position.set(0, variant.spin.y, 0); // pivot on the firing axis so the cluster spins true
      grp.add(new THREE.Mesh(variant.spin.geo, turretMat));
      barrelEnd.position.y = -variant.spin.y; // tips RIDE the spinning cluster, not the static turret
      grp.add(barrelEnd);
      turretPivot.add(grp);
      spinBarrel = grp;
    } else {
      turretPivot.add(barrelEnd);
    }
  } else {
    barrelEnd = new THREE.Mesh(geo.barrelEnd, barrelMat);
    turretPivot.add(barrelEnd);
  }
  const muzzles = variant ? variant.muzzles : [MUZZLE_LOCAL];
  return { turretPivot, turret, barrelEnd, spinBarrel, muzzles };
}

/**
 * Build one tank instance. `typeId`/`layerIdx` pick the per-type turret + body style;
 * pass none (player/ally) for the base treaded tank. Body + turret get fresh
 * per-instance materials; treads/legs/barrel end share the fixed dark materials.
 */
export function buildTank(
  geo: TankGeometries,
  color: number,
  ghost = false,
  typeId?: string,
  layerIdx = 0,
): BuiltTank {
  const root = new THREE.Group();
  const bodyMat = new THREE.MeshLambertMaterial({ color });
  const turretMat = new THREE.MeshLambertMaterial({ color });

  // ---- body: legged emplacement for stationary types, else treads + hull ----
  const isStatic = usesStaticBody(typeId);
  const treads = new THREE.Mesh(isStatic ? geo.baseLegs : geo.treads, treadsMat); // "treads" slot = dark structural base
  const body = new THREE.Mesh(isStatic ? geo.baseMain : geo.body, bodyMat);

  // ---- turret: per-type variant if any, else the base turret + barrel end ----
  const { turretPivot, turret, barrelEnd, spinBarrel, muzzles } = buildTurretPivot(geo, turretMat, typeId, layerIdx);

  // ghosts are FULLY invisible — even the structural base shows nothing. The ONLY tell
  // is the tread TRAIL on the ground (a separate system). We hide outright (near-zero
  // opacity still caught the light and read as "clearly a tank").
  if (ghost) {
    treads.visible = body.visible = turret.visible = false;
    if (barrelEnd) barrelEnd.visible = false;
    if (spinBarrel) spinBarrel.visible = false;
    treads.castShadow = false;
  } else {
    treads.castShadow = body.castShadow = turret.castShadow = true;
    if (barrelEnd) barrelEnd.castShadow = true;
    if (spinBarrel) spinBarrel.traverse((o) => { if (o instanceof THREE.Mesh) o.castShadow = true; });
    treads.receiveShadow = body.receiveShadow = true;
  }

  root.add(treads, body, turretPivot);
  return {
    root,
    turretPivot,
    bodyMat,
    turretMat,
    muzzles,
    spinBarrel,
    dispose() {
      bodyMat.dispose();
      turretMat.dispose();
    },
  };
}

const MECHA_CORNERS: [number, number][] = [[-1, -1], [1, -1], [-1, 1], [1, 1]];

/**
 * Build a MECHA walker instance: a tall body platform on 4 legs, wearing `typeId`'s turret.
 * Each leg is two rigid bone meshes (upper + lower) parented to bodyGroup; the entity orients
 * them every frame via 2-bone IK to reach a planted foot (no mirroring — one shared geo per
 * bone, oriented per corner). Legs share the fixed dark `treadsMat` (like the stationary
 * stand); base + turret tint per-type. Field names match BuiltTank so the Tank holds either.
 */
export function buildMecha(geo: TankGeometries, color: number, typeId?: string, layerIdx = 0): BuiltMecha {
  const root = new THREE.Group();
  const bodyMat = new THREE.MeshLambertMaterial({ color });
  const turretMat = new THREE.MeshLambertMaterial({ color });

  const bodyGroup = new THREE.Group();
  const base = new THREE.Mesh(geo.mecha.base, bodyMat);
  base.castShadow = true; base.receiveShadow = true;
  bodyGroup.add(base);

  const hx = Math.abs(MECHA_HIP.x), hz = Math.abs(MECHA_HIP.z); // hip attaches at each base corner
  // neutral foot spread, pulled IN from the modeled max-reach so the legs rest BENT — leaving
  // room to take strides (without over-extending the IK) and to straighten UP toward a road.
  const fx = Math.abs(MECHA_FOOT.x) * 0.82, fz = Math.abs(MECHA_FOOT.z) * 0.82;
  const legs: MechaLeg[] = [];
  for (const [tx, tz] of MECHA_CORNERS) {
    const upper = new THREE.Mesh(geo.mecha.up, treadsMat); upper.castShadow = true;
    const lower = new THREE.Mesh(geo.mecha.lo, treadsMat); lower.castShadow = true;
    bodyGroup.add(upper, lower);
    legs.push({
      upper, lower, corner: [tx, tz],
      hipLocal: new THREE.Vector3(tx * hx, MECHA_HIP.y, tz * hz),
      restFoot: new THREE.Vector3(tx * fx, 0, tz * fz),
      planted: new THREE.Vector3(), stepFrom: new THREE.Vector3(), stepTo: new THREE.Vector3(),
      stepping: false, stepT: 0, dodge: false, alive: true,
    });
  }

  const { turretPivot, turret, barrelEnd, spinBarrel, muzzles } = buildTurretPivot(geo, turretMat, typeId, layerIdx);
  turretPivot.position.y = MECHA_TURRET_Y; // sits on the base platform top
  turret.castShadow = true;
  barrelEnd.castShadow = true;
  if (spinBarrel) spinBarrel.traverse((o) => { if (o instanceof THREE.Mesh) o.castShadow = true; });
  bodyGroup.add(turretPivot);
  root.add(bodyGroup);

  return {
    root, bodyGroup, turretPivot, legs, bodyMat, turretMat, muzzles, spinBarrel,
    l1: MECHA_L1, l2: MECHA_L2, upAxis: MECHA_UP_AXIS, loAxis: MECHA_LO_AXIS, restNormal: MECHA_REST_NORMAL,
    dispose() { bodyMat.dispose(); turretMat.dispose(); },
  };
}
